import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges, ViewChild
} from "@angular/core";

import { CommonModule } from "@angular/common";
import { NgApexchartsModule } from "ng-apexcharts";
import { CoreService } from "../../../services/core.service";
import { forkJoin, of } from "rxjs";
import { ChartComponent } from "ng-apexcharts";
import { CreditSummaryResponse } from "../../../interfaces/core.interface";

@Component({
  selector: "app-social-ai-format-breakdown-chart",
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  templateUrl: "./social-ai-format-breakdown-chart.component.html",
  styleUrl: "./social-ai-format-breakdown-chart.component.scss",
})
export class SocialAiFormatBreakdownChartComponent
  implements OnInit, OnChanges {
  @Input() selectedSiteId: number | null = null;
  @Input() selectedPlatformId: number | null = null;
  @Input() selectedPeriod: string | null = null;
  @Input() platforms: any[] = [];
  @Input() pages: any[] = [];
  @Input() title: string = "";
  @ViewChild("chartRef", { static: false })
  chart!: ChartComponent;

  companyID: number | null = null;

  isLoaded = false;
  showSaves = false;

  // Credit Summary Properties
  isCreditLoading = false;
  creditSummary: CreditSummaryResponse['data'] | null = null;
  creditError = false;

  chartOptions: any = {};
  formats: any[] = [];

  constructor(private core: CoreService) { }

  ngOnInit() {
    this.loadCreditSummary();
  }

  loadCreditSummary() {
    this.isCreditLoading = true;
    this.creditError = false;
    this.core.getCreditSummary().subscribe({
      next: (response: CreditSummaryResponse) => {
        this.isCreditLoading = false;
        if (response.success) {
          this.creditSummary = response.data;
        } else {
          this.creditError = true;
        }
      },
      error: (error) => {
        this.isCreditLoading = false;
        this.creditError = true;
        console.error('Error loading credit summary:', error);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["selectedSiteId"] ||
      changes["selectedPlatformId"] ||
      changes["selectedPeriod"]
    ) {
      this.fetchData();
    }
  }

  // ---------------------------
  // PLATFORM HELPERS
  // ---------------------------
  isFacebook(): boolean {
    const p = this.platforms.find((x) => x.id === this.selectedPlatformId);
    return p?.platform?.toLowerCase().includes("facebook") ?? false;
  }

  isInstagram(): boolean {
    const p = this.platforms.find((x) => x.id === this.selectedPlatformId);
    return p?.platform?.toLowerCase().includes("instagram") ?? false;
  }

  isTikTok(): boolean {
    const p = this.platforms.find((x) => x.id === this.selectedPlatformId);
    return p?.platform?.toLowerCase().includes("tiktok") ?? false;
  }

  // ---------------------------
  // TAG NORMALIZER
  // ---------------------------
  private normalizeTag(tag: string): string {
    tag = tag.toLowerCase();

    if (tag === "photolink") return "linkpost";

    if (
      tag.includes("photopost") ||
      tag.includes("fbphotopost") ||
      tag.includes("igphotopost") ||
      tag.includes("fbfirstcomment")
    ) {
      return "photopost";
    }

    if (tag.includes("igcarousel") || tag.includes("tiktokcarousel")) {
      return "carousel";
    }

    if (
      tag.includes("fbvideo") ||
      tag.includes("igvideo") ||
      tag.includes("tiktokvideo")
    ) {
      return "reel";
    }

    return tag;
  }

  // ---------------------------
  // API FETCH
  // ---------------------------
  fetchData() {
    this.companyID = this.core.user?.companyID || 0;
    if (!this.selectedSiteId || !this.selectedPlatformId) return;

    this.isLoaded = false;

    const platform = this.platforms.find((p) => p.id === this.selectedPlatformId);
    const isIG = platform?.platform?.toLowerCase().includes("instagram");
    this.showSaves = isIG;

    const payload = {
      contentsite_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      period: this.selectedPeriod,
      metrics: ['engagement', 'ctr'],
    };

    const metrics$ = this.core.postData(`querymetrics/format-breakdown/${this.companyID}`, payload);
    const saves$ = isIG
      ? this.core.postData(`getsavesrate/${this.companyID}`, payload)
      : of(null);

    forkJoin({ metrics: metrics$, saves: saves$ }).subscribe(({ metrics, saves }) =>
      this.processData(metrics, saves)
    );
  }

  // ---------------------------
  // INITIAL BUCKET
  // ---------------------------
  private createBucket(label: string) {
    return {
      name: label,
      engagement: 0,
      engagementValue: 0,
      saves: null,
      savesValue: 0,
      clicks: 0,
      clicksValue: 0,
    };
  }

  // ---------------------------
  // PROCESS RESPONSE DATA
  // ---------------------------
  processData(metricsRes: any, sav: any) {
    const pid = this.selectedPlatformId!;
    const metricsData = metricsRes?.[pid] ?? {};
    const savData = sav?.[pid] ?? {};

    const groups: any = {
      photopost: this.createBucket("Photo Posts"),
      linkpost: this.createBucket("Link Posts"),
      reel: this.createBucket("Reels"),
      carousel: this.createBucket("Carousels"),
    };

    Object.keys(metricsData).forEach((tag) => {
      const norm = this.normalizeTag(tag);
      if (!groups[norm]) return;

      const tagData = metricsData[tag];

      // ENGAGEMENT
      if (tagData.engagement) {
        groups[norm].engagement += Number(parseFloat(tagData.engagement.engagement_rate || 0));
        groups[norm].engagementValue += Number(parseFloat(tagData.engagement.total_engagement || 0));
      }

      // CLICKS
      if (tagData.ctr) {
        groups[norm].clicks += Number(parseFloat(tagData.ctr.ctr || 0));
        groups[norm].clicksValue += Number(parseFloat(tagData.ctr.total_clicks || 0));
      }

      // SAVES (IG ONLY)
      if (savData[tag]) {
        groups[norm].saves = Number(parseFloat(savData[tag].saves_rate || 0));
        groups[norm].savesValue = Number(parseFloat(savData[tag].total_saves || 0));
      }
    });

    if (this.isFacebook()) delete groups["carousel"];
    if (this.isInstagram()) delete groups["linkpost"];

    this.formats = Object.values(groups);
    this.buildChart();
  }

  // buildChart() {
  //   const clicksPercent = this.formats.map(f =>
  //     f.engagementValue > 0
  //       ? (f.clicksValue / f.engagementValue) * 100
  //       : 0
  //   );

  //   const restEngagementPercent = this.formats.map(f =>
  //     f.engagementValue > 0
  //       ? 100 - (f.clicksValue / f.engagementValue) * 100
  //       : 0
  //   );

  //   this.chartOptions = {
  //     chart: {
  //       type: "bar",
  //       stacked: true,
  //       height: 330,
  //       toolbar: { show: false },
  //     },

  //     series: [
  //       {
  //         name: "Clicks",
  //         data: clicksPercent,
  //       },
  //       {
  //         name: "Other Engagement",
  //         data: restEngagementPercent,
  //       },
  //     ],

  //     colors: ["#10b981", "#2563eb"],

  //     plotOptions: {
  //       bar: {
  //         horizontal: false,
  //         columnWidth: "18%",
  //         borderRadius: 6,
  //       },
  //     },

  //     dataLabels: {
  //       enabled: true,
  //       formatter: (val: number) => `${val.toFixed(1)}%`,
  //     },

  //     yaxis: {
  //       max: 100,
  //       labels: {
  //         formatter: (val: number) => `${val.toFixed(0)}%`,
  //       },
  //     },
  //     xaxis: {
  //       categories: this.formats.map(f => f.name),
  //       labels: {
  //         style: {
  //           fontSize: "13px",
  //           colors: "#374151",
  //           fontWeight: 600,
  //         },
  //       },
  //     },
  //     tooltip: {
  //       shared: false,
  //       intersect: true,
  //       y: {
  //         formatter: (val: number, opts: any) => {
  //           const f = this.formats[opts.dataPointIndex];

  //           if (opts.seriesIndex === 0) {
  //             return `${val.toFixed(2)}% (${f.clicksValue.toLocaleString("en-US")} clicks)`;
  //           }

  //           return `${val.toFixed(1)}% (${f.engagementValue.toLocaleString("en-US")} engagement)`;
  //         },
  //       },
  //     },
  //     legend: {
  //       show: false,
  //     },

  //     grid: { show: false },
  //   };

  //   this.isLoaded = true;
  // }


  //(DUAL Y AXES)
  buildChart() {
    this.chartOptions = {
      tooltip: {
        enabled: true,
        shared: false,
        intersect: false,
        y: {
          formatter: (val: number) => val.toLocaleString("en-US")
        }
      },
      series: [
        {
          name: "Engagement",
          type: "column",
          data: this.formats.map(f => f.engagementValue),
        },
        {
          name: "Clicks",
          type: "column",
          data: this.formats.map(f => f.clicksValue),
        },
      ],

      colors: ["#2563eb", "#10b981"],

      chart: {
        type: "bar",
        height: 320,
        toolbar: { show: false },
      },

      legend: {
        show: false,
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: { height: 220 },
            grid: { padding: { bottom: -30 } }
          }
        }
      ],

      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "18%",
          borderRadius: 6,
        },
      },

      dataLabels: { enabled: false },

      yaxis: [
        {
          // title: { text: "Engagement" },
          labels: {
            formatter: (val: number) =>
              val === 0 ? "" : (val / 100).toFixed(2),
          },
        },
        {
          opposite: true,
          // title: { text: "Clicks" },
          labels: {
            formatter: (val: number) =>
              val === 0 ? "" : (val / 1000).toFixed(3),
          },
        },
      ],

      // --------------------------------------
      // X AXIS
      // --------------------------------------
      xaxis: {
        categories: this.formats.map(f => f.name),
        labels: {
          style: {
            fontSize: "13px",
            colors: "#374151",
            fontWeight: 600,
          },
        },
      },

      // grid: { show: false },
      grid: {
        show: false,
        padding: { top: 0, right: 0, bottom: -15, left: 0 },
      },
    };

    this.isLoaded = true;
  }

    async exportChartImage(): Promise<string> {

    if (!this.chart) {
      throw new Error('Reach chart not ready');
    }

    const data = await this.chart.dataURI();
    return data.imgURI;
  }
}
